import {
  CANDIDATE_RESOURCE,
  VIDEO_DELIVERY_RESOURCE,
  type StudioEvent,
  type VideoCandidateResource,
  type VideoShelfResource,
} from '@studio/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { subscribeToEvents } from '../../shell/stream.js';

/**
 * The video shelf and one open Video Candidate, kept level with the server.
 *
 * The order is the shell's: open the stream first and buffer, read the snapshot, then
 * act only on what is newer. A render takes real time, so the Animatic that appears and
 * the MP4 that finishes both arrive while the operator is looking at something else -
 * neither may wait for a reload.
 */

const WATCHED: readonly string[] = [CANDIDATE_RESOURCE, VIDEO_DELIVERY_RESOURCE];

/**
 * One version line per resource, keyed by resource and id together.
 *
 * A Candidate and its two deliveries are three rows with three independent versions.
 * Keying by id alone would file them under one number, and the smaller of two unrelated
 * versions would look like an event already seen.
 */
function versionsOf(candidates: readonly VideoCandidateResource[]): ReadonlyMap<string, number> {
  const versions = new Map<string, number>();
  for (const candidate of candidates) {
    versions.set(`${CANDIDATE_RESOURCE}:${candidate.candidateId}`, candidate.version);
    for (const delivery of [candidate.animatic, candidate.render]) {
      if (delivery !== null) versions.set(`${VIDEO_DELIVERY_RESOURCE}:${delivery.id}`, delivery.version);
    }
  }
  return versions;
}

export function needsRefetch(versions: ReadonlyMap<string, number>, event: StudioEvent): boolean {
  if (!WATCHED.includes(event.resource)) return false;
  const standing = versions.get(`${event.resource}:${event.id}`);
  return standing === undefined || event.version > standing;
}

interface Live<T> {
  readonly value: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly refresh: () => void;
}

function useLive<T>(
  key: string,
  read: () => Promise<T>,
  candidatesOf: (value: T) => readonly VideoCandidateResource[],
  failure: string,
): Live<T> {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const versions = useRef<ReadonlyMap<string, number>>(new Map());
  const [nonce, setNonce] = useState(0);
  const load = useRef(read);
  load.current = read;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (key === '') return;
    let live = true;
    let snapshotApplied = false;
    const buffered: StudioEvent[] = [];
    // One read at a time, and one more afterwards if anything arrived while it ran: a
    // render publishes several events in a moment, and reads that overlap come back in
    // whatever order the network gives them.
    let inFlight = false;
    let again = false;

    const readOnce = (): void => {
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      load
        .current()
        .then((next) => {
          if (!live) return;
          versions.current = versionsOf(candidatesOf(next));
          setValue(next);
          setError(null);
          snapshotApplied = true;
          if (buffered.splice(0).some((event) => needsRefetch(versions.current, event))) again = true;
        })
        .catch((caught: unknown) => {
          if (!live) return;
          setError(caught instanceof ApiError ? caught.message : failure);
          snapshotApplied = true;
        })
        .finally(() => {
          inFlight = false;
          if (live) setLoading(false);
          if (!again || !live) return;
          again = false;
          readOnce();
        });
    };

    const unsubscribe = subscribeToEvents((event) => {
      if (!live) return;
      if (!snapshotApplied) {
        buffered.push(event);
        return;
      }
      if (needsRefetch(versions.current, event)) readOnce();
    });

    setLoading(true);
    readOnce();

    return () => {
      live = false;
      unsubscribe();
    };
  }, [key, nonce, candidatesOf, failure]);

  return { value, error, loading, refresh };
}

const shelfCandidates = (shelf: VideoShelfResource): readonly VideoCandidateResource[] => shelf.candidates;
const oneCandidate = (candidate: VideoCandidateResource): readonly VideoCandidateResource[] => [candidate];

export function useVideoShelf(experimentId: string): Live<VideoShelfResource> {
  return useLive(
    experimentId,
    useCallback(() => api.readVideoShelf(experimentId), [experimentId]),
    shelfCandidates,
    'Video shelf не удалось прочитать.',
  );
}

export function useVideoCandidate(candidateId: string): Live<VideoCandidateResource> {
  return useLive(
    candidateId,
    useCallback(() => api.readVideoCandidate(candidateId), [candidateId]),
    oneCandidate,
    'Этот Video Candidate не удалось прочитать.',
  );
}

/** `00:20`, the way the Scene ribbon and the player label a length. */
export function clock(totalMs: number): string {
  const seconds = Math.max(0, Math.round(totalMs / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
